 import React, { useContext } from 'react';
import Divider from '@material-ui/core/Divider';
import NestedMenuItem from 'material-ui-nested-menu-item';
import MenuItem from '@material-ui/core/MenuItem';
import Menu from '@material-ui/core/Menu';
import EditorContext from '@mirinae/lessons/editor/modules/contexts/editor';
import { useMenuClasses, useMenuItemClasses } from '@mirinae/lessons/editor/components/Navigator/TreeContextMenu';
import {colorChoices, styleMap} from '@mirinae/lessons/editor/modules/defines/style';
import deepClone from "deep-clone";

const AnimContextMenu = React.forwardRef((props, ref) => {
    const { element, onClose, redraw } = props;
    const editor = useContext(EditorContext);
    const menuClasses = useMenuClasses();
    const menuItemClasses = useMenuItemClasses();

    const editData = () => {
        const { part, lang, parts, section, field, stepFields, narration, narrationIndex, e } = editor.animContextMenu.context;
        const pn = lang && `${lang}Parts`;
        return {
            part, lang, parts, section, field, stepFields, pn, narration, narrationIndex, e,
            realField: field && element.fields[field.index],
            realPart: lang && element.fields[field.index].parts[pn][part.partIndex]};
    };

    // pattern-animation editor menu ops
    const setPartSubfield = (item, subfield) => {
        const { part, realField, realPart, pn } = editData();
        part[subfield] = item.value || item.label;
        // also update real field parts
        realField.parts[pn][part.partIndex][subfield] = part[subfield];
        editor.commitNodeUpdate(element);
    };

    const duplicatePart = () => {
        const { part, realField, realPart, pn } = editData();
        // update real field & rebuild computed layout fields
        const newParts = [...realField.parts[pn]];
        newParts.splice(part.partIndex + 1, 0, deepClone(realPart));
        realField.parts[pn] = [...newParts];
        editor.commitNodeUpdate(element);
        redraw();
    };

    const deletePart = () => {
        const { part, realField, pn } = editData();
        // update real field & rebuild computed layout fields
        const newParts = [...realField.parts[pn]];
        newParts.splice(part.partIndex, 1);
        realField.parts[pn] = [...newParts];
        editor.commitNodeUpdate(element);
        redraw();
    };

    const movePart = (direction) => {
    };

    const moveSection = (direction) => {
    };

    const commands = () => {
        editor.update({
            animationEditor: { ...editor.animationEditor, showCommandsFor: editData().part },
            animContextMenu: { open: false, context: {} },
        });
    };

    const insertSection = (side) => {
        const { section, stepFields } = editData();
        element.fields.filter(f => f.stepFieldIndex !== undefined).forEach(f => {
            ['korean', 'english'].forEach(lang => {
                const pn = `${lang}Parts`;
                const newParts = [...f.parts[pn]];
                const field = stepFields[f.stepFieldIndex];
                const s = field.sections[section.index];
                const spliceIndex = side === 'left' ? s[lang].parts[0].partIndex : s[lang].parts[s[lang].parts.length - 1].partIndex + 1;
                const color = lang === 'english' ? 'english' : 'addition';
                const newSection = side === 'left'
                    ? [{ value: '', color: color }, { value: '', color: 'separate' }]
                    : [{ value: '', color: 'separate' }, { value: '', color }]
                newParts.splice(spliceIndex, 0, ...newSection);
                f.parts[pn] = [...newParts];
            });
        });
        editor.commitNodeUpdate(element);
        redraw();
    };

    const deleteSection = () => {
        const { section, stepFields } = editData();
        element.fields.filter(f => f.stepFieldIndex !== undefined).forEach(f => {
            ['korean', 'english'].forEach(lang => {
                const pn = `${lang}Parts`;
                const newParts = [...f.parts[pn]];
                const field = stepFields[f.stepFieldIndex];
                const s = field.sections[section.index];
                newParts.splice(s[lang].parts[0].partIndex - (section.index > 0 ? 1 : 0), s[lang].parts.length + 1);
                f.parts[pn] = [...newParts];
            });
        });
        editor.commitNodeUpdate(element);
        redraw();
    };

    const duplicateStep = () => {
        const { realField: f } = editData();
        const copy = { ...deepClone(f), name: `${f.name}-copy`, label: `${f.label}-copy`};
        element.fields.splice(f.index + 1, 0, copy);
        element.fields.forEach((f, i) => f.index = i);
        editor.commitNodeUpdate(element);
        redraw();
    }

    const deleteStep = () => {
        const { realField: f } = editData();
        element.fields.splice(f.index, 1);
        element.fields.forEach((f, i) => f.index = i);
        editor.commitNodeUpdate(element);
        redraw();
    }

    const moveStepUp = () => {
        const { realField: f } = editData();
        element.fields.splice(f.index, 1);
        element.fields.splice(f.index - 1, 0, f);
        element.fields.forEach((f, i) => f.index = i);
        editor.commitNodeUpdate(element);
        redraw();
    }

    const moveStepDown = () => {
        const { realField: f } = editData();
        element.fields.splice(f.index, 1);
        element.fields.splice(f.index + 1, 0, f);
        element.fields.forEach((f, i) => f.index = i);
        editor.commitNodeUpdate(element);
        redraw();
    }

    const hideStep = () => {
        const { realField: f } = editData();
        f.hidden = !f.hidden;
        editor.commitNodeUpdate(element);
        redraw();
    }

    const unhideAll = () => {
        element.fields.filter(f => f.stepFieldIndex !== undefined).forEach(f => f.hidden = false);
        editor.commitNodeUpdate(element);
        redraw();
    }

    const insertBubbleAbove = (index) => {
        const { realField, narrationIndex } = editData();
        realField.parts.narration.splice(narrationIndex, 0, { links: [], styles: [], value: ""});
        editor.commitNodeUpdate(element);
        redraw();
    }

    const insertBubbleBelow = () => {
        const { realField, narrationIndex } = editData();
        realField.parts.narration.splice(narrationIndex + 1, 0, { links: [], styles: [], value: ""});
        editor.commitNodeUpdate(element);
        redraw();
    }

    const deleteBubble = () => {
        const { realField, narrationIndex } = editData();
        realField.parts.narration.splice(narrationIndex, 1);
        editor.commitNodeUpdate(element);
        redraw();
    }

    const editStyle = (style) => {
        const { realField, narrationIndex, e } = editData();
        const el = e.target;
        const selectedText = el.value.substring(el.selectionStart, el.selectionEnd);
        const narration = realField.parts.narration[narrationIndex];
        var thisStyle = narration.styles.find(l => l.styledText === selectedText && l.start === el.selectionStart);
        if (!thisStyle) {
            thisStyle = { start: el.selectionStart, end: el.selectionEnd, styledText: selectedText, style };
            narration.styles.push(thisStyle);
        } else {
            thisStyle.style = style;
        }
        editor.commitNodeUpdate(element);
        redraw();
    }

    const clearStyles = () => {
        const { realField, narrationIndex, e } = editData();
        const narration = realField.parts.narration[narrationIndex];
        narration.styles = [];
        editor.commitNodeUpdate(element);
    }

    const menuItems = () => {
        const { realField, stepFields, field, parts, e } = editData();
        let items = [];
        if (realField.stepFieldIndex === 0 && field.label === 'Example')
            items = [{label: '-- Example step locked --', disable: () => true, handler: () => {}}]
        else {
            items = [
                { label: 'Duplicate step', handler: duplicateStep },
                { label: 'Delete step',  handler: deleteStep },
                { label: 'Move step up',
                    disable: () => realField.stepFieldIndex <= 0 || realField.stepFieldIndex === 1 && stepFields[0].label === 'Example', handler: moveStepUp },
                { label: 'Move step down', disable: () => realField.stepFieldIndex >= stepFields.length, handler: moveStepDown },
                { label: 'Hide step', handler: hideStep },
            ];
        }
        if (element.fields.some(f => f.parts && f.hidden))
            items.push({ label: 'Unhide hidden steps', handler: unhideAll });
        if (editor.animContextMenu.context.section) {
            items = [
                { label: 'Insert section left', handler: () => insertSection('left') },
                { label: 'Insert section right', handler: () => insertSection('right') },
                // { label: 'Move section contents left', handler: () => moveSection('left') },
                // { label: 'Move section contents right', handler: () => moveSection('right') },
                { label: 'Delete section', disable: () => field.sections.length <= 1, handler: deleteSection },
            ].concat([
                { divider: true },
                ...items
            ])
        }
        if (editor.animContextMenu.context.part) {
            items = [
                { label: 'Part color', submenu: colorChoices.map(c =>
                        ({ label: c, selected: (ed) => ed.part && ed.part.color === c, handler: (item) => setPartSubfield(item, 'color') })) },
                { label: 'Commands', handler: commands },
                { divider: true },
                { label: 'Duplicate part', handler: duplicatePart },
                // { label: 'Move part to next section', handler: movePart('right') },
                // { label: 'Move part to prior section', handler: movePar('left') },
                { label: 'Delete part', disable: () => parts.length <= 1, handler: deletePart },

                // { label: 'Add part command', submenu: []},
            ].concat([
                { divider: true },
                ...items
            ]);
        }
        if (editor.animContextMenu.context.narration) {
            items = [
                { label: 'Insert bubble above', handler: insertBubbleAbove },
                { label: 'Insert bubble below', handler: insertBubbleBelow },
                { label: 'Delete bubble',  disable: () => field.parts.narration.length <= 1, handler: deleteBubble },
                { divider: true },
                { label: 'Edit style',  disable: () => e.target.selectionEnd - e.target.selectionStart <= 0,
                    submenu: Object.getOwnPropertyNames(styleMap).sort().map(s =>
                        ({ label: s, handler: (item) => editStyle(item.label) }))},
                { label: 'Clear styles', handler: clearStyles },
            ].concat([
                { divider: true },
                ...items
            ]);
        }

        return items;
    };

    const MenuEntry = React.forwardRef((props, ref) => {
        const { item, open } = props;
        return (
            (item.divider || item.label === '-' ? (
                <Divider />
            ) : (item.submenu ? (
                <NestedMenuItem classes={menuItemClasses} label={item.label} parentMenuOpen={open}>
                    {item.submenu.map((subitem, i) => <MenuEntry key={i} item={subitem} open={open} />)}
                </NestedMenuItem>
            ) : (
                <MenuItem
                    classes={menuItemClasses}
                    disabled={item.disable ? item.disable(item) : false}
                    selected={item.selected && item.selected(editData())}
                    onClick={(e) => {
                        item.handler(item, e);
                        onClose();
                        editor.update({ animContextMenu: { open: false, context: {} } });
                    }}
                >
                    {typeof item.label === 'function' ? item.label(item) : item.label}
                </MenuItem>
            )))
        );
    });

    return (
        <Menu
            ref={ref}
            classes={menuClasses}
            open={editor.animContextMenu.open}
            onClose={() => { onClose(); editor.update({ animContextMenu: { open: false, context: {} } }); }}
            anchorReference="anchorPosition"
            anchorPosition={{ top: editor.animContextMenu.context.mouseY + 10, left: editor.animContextMenu.context.mouseX - 30 }}
            // transformOrigin={{ vertical: -20, 'horizontal': -10 }}
        >
            { menuItems().map((item, i) => <MenuEntry key={i} item={item} open={!!editor.animContextMenu.open} />)}
        </Menu>
    );
});

export default AnimContextMenu;